import React, { useEffect, useState, useCallback } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  Toggle,
  Modal,
  Pagination,
  InlineLoading,
  InlineNotification,
  Dropdown,
} from "@carbon/react";
import { Add, TrashCan } from "@carbon/icons-react";
import { callMongoApi } from "../../services/mongoApiService";

const MAX_IMAGE_SIZE_MB = 5;

const PLATFORM_OPTIONS = [
  { id: "website", label: "Website" },
  { id: "mobile", label: "Mobile App" },
];

export default function BannerManagement() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const [searchValue, setSearchValue] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [propertySearch, setPropertySearch] = useState("");
  const [properties, setProperties] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  // Platform selector: "website" | "mobile"
  const [platform, setPlatform] = useState("website");

  // Single image (goes to "image" or "mobileImage" based on platform)
  const [bannerImage, setBannerImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const [newBanner, setNewBanner] = useState({
    title: "",
    description: "",
    displayOrder: 0,
    featured: false,
    display: true,
    imageUrl: "",
    propertyUuid: "",
  });

  // ── Fetch banners ───────────────────────────────────────────
  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      setApiError("");

      const response = await callMongoApi("GET", "/banners");
      const data = response?.data;

      setBanners(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch banners:", error);
      setApiError("Failed to load banners.");
      setBanners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  // ── Image change (no compression) ───────────────────────────
  const handleBannerImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setApiError("Please select a valid image file.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      setApiError(
        `Image "${file.name}" exceeds ${MAX_IMAGE_SIZE_MB}MB limit.`
      );
      event.target.value = "";
      return;
    }

    setApiError("");

    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setBannerImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // ── Search ──────────────────────────────────────────────────
  const filteredBanners = banners.filter((banner) => {
    const search = searchValue.toLowerCase();
    return (
      String(banner?.title || "").toLowerCase().includes(search) ||
      String(banner?.description || "").toLowerCase().includes(search)
    );
  });

  // ── Delete ──────────────────────────────────────────────────
  const handleDeleteBanner = async () => {
    const bannerId = selectedBanner?.id || selectedBanner?.uuid;
    if (!bannerId) return;

    try {
      setDeleting(true);
      await callMongoApi("DELETE", `/banners/${bannerId}`);
      setBanners((prev) =>
        prev.filter((b) => (b.id || b.uuid) !== bannerId)
      );
      setDeleteModalOpen(false);
      setSelectedBanner(null);
    } catch (error) {
      console.error("Failed to delete banner:", error);
      setApiError("Failed to delete banner.");
    } finally {
      setDeleting(false);
    }
  };

  // ── Display toggle ──────────────────────────────────────────
  const handleDisplayToggle = async (banner) => {
    const bannerId = banner?.id || banner?.uuid;
    if (!bannerId) return;

    const newActiveValue = !banner.isActive;

    try {
      await callMongoApi("PUT", `/banners/${bannerId}`, {
        isActive: newActiveValue,
      });
      setBanners((prev) =>
        prev.map((item) =>
          (item.id || item.uuid) === bannerId
            ? { ...item, isActive: newActiveValue }
            : item
        )
      );
    } catch (error) {
      console.error("Failed to update banner active status:", error);
      setApiError("Failed to update banner display status.");
    }
  };

  // ── Property search ─────────────────────────────────────────
  const searchProperties = useCallback(async (search) => {
    const value = search.trim().toLowerCase();
    if (!value) {
      setProperties([]);
      return;
    }

    try {
      setPropertyLoading(true);

      const response = await callMongoApi(
        "GET",
        `/properties?search=${encodeURIComponent(value)}`
      );

      const data = Array.isArray(response?.data) ? response.data : [];

      const sorted = [...data].sort((a, b) => {
        const aSeq = String(a?.property_seq_id || "").toLowerCase();
        const bSeq = String(b?.property_seq_id || "").toLowerCase();
        const aUuid = String(a?.uuid || "").toLowerCase();
        const bUuid = String(b?.uuid || "").toLowerCase();

        if (aSeq === value && bSeq !== value) return -1;
        if (bSeq === value && aSeq !== value) return 1;

        if (aSeq.startsWith(value) && !bSeq.startsWith(value)) return -1;
        if (bSeq.startsWith(value) && !aSeq.startsWith(value)) return 1;

        if (aUuid === value && bUuid !== value) return -1;
        if (bUuid === value && aUuid !== value) return 1;

        if (aUuid.startsWith(value) && !bUuid.startsWith(value)) return -1;
        if (bUuid.startsWith(value) && !aUuid.startsWith(value)) return 1;

        return 0;
      });

      setProperties(sorted);
    } catch (error) {
      console.error("Failed to search properties:", error);
      setProperties([]);
    } finally {
      setPropertyLoading(false);
    }
  }, []);

  // ── Reset form ──────────────────────────────────────────────
  const resetForm = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setBannerImage(null);
    setImagePreview("");
    setPlatform("website");

    setNewBanner({
      title: "",
      description: "",
      displayOrder: 0,
      featured: false,
      display: true,
      imageUrl: "",
      propertyUuid: "",
    });

    setPropertySearch("");
    setProperties([]);
    setSelectedProperty(null);
  };

  // ── Add banner ──────────────────────────────────────────────
  const handleAddBanner = async () => {
    if (!newBanner.title.trim()) {
      setApiError("Banner title is required.");
      return;
    }

    if (!selectedProperty || !newBanner.propertyUuid) {
      setApiError("Please select a property.");
      return;
    }

    if (!platform) {
      setApiError("Please choose a platform (Website or Mobile App).");
      return;
    }

    if (!bannerImage) {
      setApiError("Please upload a banner image.");
      return;
    }

    try {
      setAdding(true);
      setApiError("");

      const formData = new FormData();
      formData.append("title", newBanner.title);
      formData.append("description", newBanner.description);
      formData.append("propertyUuid", newBanner.propertyUuid);
      formData.append("displayOrder", Number(newBanner.displayOrder));
      formData.append("isActive", String(newBanner.display));
      formData.append("isFeatured", String(newBanner.featured));

      // Route the file to the correct field based on the chosen platform
      if (platform === "website") {
        formData.append("image", bannerImage);
        formData.append("imageType", "website");
      } else {
        formData.append("mobileImage", bannerImage);
        formData.append("imageType", "mobile");
      }

      const response = await callMongoApi("POST", "/banners", formData);
      console.log("Created banner:", response);

      setAddModalOpen(false);
      resetForm();
      await fetchBanners();
    } catch (error) {
      console.error("Failed to create banner:", error);

      if (error?.status === 413 || error?.response?.status === 413) {
        setApiError(
          "The image is too large for the server. Please upload a smaller image."
        );
      } else {
        setApiError(error?.message || "Failed to create banner.");
      }
    } finally {
      setAdding(false);
    }
  };

  // ── Table rows ──────────────────────────────────────────────
  const headers = [
    { key: "banner", header: "Banner" },
    { key: "platform", header: "Platform" },
    { key: "property", header: "Property" },
    { key: "display", header: "Display" },
    { key: "actions", header: "Actions" },
  ];

  const rows = filteredBanners.map((banner) => {
    const type = banner.imageType;
    let platformLabel = "—";
    if (type === "website") platformLabel = "Website";
    else if (type === "mobile") platformLabel = "Mobile App";
    else if (type === "both") platformLabel = "Both";

    return {
      id: banner.id || banner.uuid,
      banner,
      platform: platformLabel,
      property:
        typeof banner.property === "object"
          ? banner.property?.title || "No Property"
          : banner.property || "No Property",
      display: banner.isActive,
      actions: banner,
    };
  });

  const startIndex = (page - 1) * pageSize;
  const paginatedRows = rows.slice(startIndex, startIndex + pageSize);

  return (
    <>
      <DataTable rows={paginatedRows} headers={headers} isSortable>
        {({ rows, headers, getHeaderProps, getRowProps }) => (
          <TableContainer title="Banner Management">
            <TableToolbar>
              <TableToolbarContent>
                <TableToolbarSearch
                  placeholder="Search banners..."
                  value={searchValue}
                  onChange={(event) => {
                    setSearchValue(event.target.value);
                    setPage(1);
                  }}
                />
                <Button
                  kind="primary"
                  renderIcon={Add}
                  onClick={() => {
                    setAddModalOpen(true);
                    setApiError("");
                    setPropertySearch("");
                    setProperties([]);
                    setSelectedProperty(null);
                    setPlatform("website");
                  }}
                >
                  Add Banner
                </Button>
              </TableToolbarContent>
            </TableToolbar>

            {apiError && (
              <InlineNotification
                kind="error"
                title="Error"
                subtitle={apiError}
                lowContrast
              />
            )}

            {loading ? (
              <InlineLoading description="Loading banners..." />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => (
                      <TableHeader
                        key={header.key}
                        {...getHeaderProps({ header })}
                      >
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length}>
                        No banners found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const banner = row.cells.find((cell) =>
                        cell.id.endsWith(":banner")
                      )?.value;

                      const thumb =
  banner?.imageUrl ||
  banner?.mobileImageUrl ||
  banner?.fullMobileImageUrl;

                      return (
                        <TableRow key={row.id} {...getRowProps({ row })}>
                          <TableCell>
                            <div>
                              <strong>{banner?.title || "-"}</strong>
                              {thumb && (
                                <img
                                  src={thumb}
                                  alt={banner?.title || "Banner"}
                                  style={{
                                    width: "100px",
                                    height: "50px",
                                    objectFit: "cover",
                                    display: "block",
                                    marginTop: "8px",
                                  }}
                                />
                              )}
                            </div>
                          </TableCell>

                          <TableCell>
                            {
                              row.cells.find((cell) =>
                                cell.id.endsWith(":platform")
                              )?.value
                            }
                          </TableCell>

                          <TableCell>
                            {
                              row.cells.find((cell) =>
                                cell.id.endsWith(":property")
                              )?.value
                            }
                          </TableCell>

                          <TableCell>
                            <Toggle
                              id={`display-${row.id}`}
                              size="sm"
                              toggled={
                                row.cells.find((cell) =>
                                  cell.id.endsWith(":display")
                                )?.value === true
                              }
                              labelA="No"
                              labelB="Yes"
                              onToggle={() => handleDisplayToggle(banner)}
                            />
                          </TableCell>

                          <TableCell>
                            <Button
                              kind="ghost"
                              size="sm"
                              hasIconOnly
                              renderIcon={TrashCan}
                              iconDescription="Delete banner"
                              onClick={() => {
                                setSelectedBanner(banner);
                                setDeleteModalOpen(true);
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}

            <Pagination
              page={page}
              pageSize={pageSize}
              pageSizes={[10, 20, 50]}
              totalItems={filteredBanners.length}
              onChange={({ page, pageSize }) => {
                setPage(page);
                setPageSize(pageSize);
              }}
            />
          </TableContainer>
        )}
      </DataTable>

      <Modal
        open={deleteModalOpen}
        danger
        modalHeading="Delete Banner"
        primaryButtonText={deleting ? "Deleting..." : "Delete"}
        secondaryButtonText="Cancel"
        onRequestClose={() => {
          if (!deleting) {
            setDeleteModalOpen(false);
            setSelectedBanner(null);
          }
        }}
        onSecondarySubmit={() => {
          setDeleteModalOpen(false);
          setSelectedBanner(null);
        }}
        onRequestSubmit={handleDeleteBanner}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{selectedBanner?.title || "this banner"}</strong>?
        </p>
      </Modal>

      <Modal
        open={addModalOpen}
        modalHeading="Add Banner"
        primaryButtonText={adding ? "Adding..." : "Add Banner"}
        secondaryButtonText="Cancel"
        onRequestClose={() => {
          if (!adding) setAddModalOpen(false);
        }}
        onSecondarySubmit={() => {
          if (!adding) setAddModalOpen(false);
        }}
        onRequestSubmit={handleAddBanner}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label
              htmlFor="banner-title"
              style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}
            >
              Banner Title
            </label>
            <input
              id="banner-title"
              type="text"
              value={newBanner.title}
              onChange={(e) =>
                setNewBanner((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="Enter banner title"
              style={{ width: "100%", padding: "10px", boxSizing: "border-box" }}
            />
          </div>

          <div>
            <label
              htmlFor="banner-description"
              style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}
            >
              Description
            </label>
            <textarea
              id="banner-description"
              value={newBanner.description}
              onChange={(e) =>
                setNewBanner((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Enter banner description"
              rows={4}
              style={{ width: "100%", padding: "10px", boxSizing: "border-box" }}
            />
          </div>

          <div>
            <label
              style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}
            >
              Platform <span style={{ color: "#da1e28" }}>*</span>
            </label>

            <Dropdown
              id="platform-dropdown"
              titleText=""
              label="Choose platform"
              items={PLATFORM_OPTIONS}
              itemToString={(item) => (item ? item.label : "")}
              selectedItem={
                PLATFORM_OPTIONS.find((p) => p.id === platform) || null
              }
              onChange={({ selectedItem }) =>
                setPlatform(selectedItem?.id || "website")
              }
              disabled={adding}
            />

            <div
              style={{ marginTop: "6px", fontSize: "12px", color: "#525252" }}
            >
              {platform === "website"
                ? "This banner will be shown on the website only."
                : "This banner will be shown in the mobile app only."}
            </div>
          </div>

          <div>
            <label
              htmlFor="property-search"
              style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}
            >
              Property
            </label>

            <input
              id="property-search"
              type="text"
              value={propertySearch}
              onChange={(e) => {
                const value = e.target.value;
                setPropertySearch(value);

                if (!value.trim()) {
                  setProperties([]);
                  return;
                }
                searchProperties(value);
              }}
              placeholder="Enter Property Sequence ID or UUID"
              disabled={adding}
              style={{ width: "100%", padding: "10px", boxSizing: "border-box" }}
            />

            {propertyLoading && (
              <div style={{ marginTop: "10px" }}>
                <InlineLoading description="Searching property..." />
              </div>
            )}

            {!propertyLoading &&
              propertySearch.trim() &&
              properties.length > 0 &&
              !selectedProperty && (
                <div
                  style={{
                    marginTop: "8px",
                    border: "1px solid #dcdcdc",
                    maxHeight: "250px",
                    overflowY: "auto",
                    background: "#fff",
                  }}
                >
                  {properties.map((property) => {
                    const uuid = property.uuid || property.propertyUuid;
                    const sequenceId =
                      property.property_seq_id || property.property_sequence_id;
                    const title =
                      property.title ||
                      property.property_title ||
                      "Untitled Property";

                    return (
                      <div
                        key={uuid || sequenceId}
                        onClick={() => {
                          setSelectedProperty(property);
                          setPropertySearch(sequenceId || "");
                          setNewBanner((prev) => ({
                            ...prev,
                            propertyUuid: uuid,
                          }));
                          setProperties([]);
                        }}
                        style={{
                          padding: "12px",
                          borderBottom: "1px solid #e0e0e0",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: "5px" }}>
                          {title}
                        </div>
                        <div style={{ fontSize: "13px", color: "#525252" }}>
                          Sequence ID: {sequenceId || "-"}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#525252",
                            wordBreak: "break-all",
                          }}
                        >
                          UUID: {uuid || "-"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

            {!propertyLoading &&
              propertySearch.trim() &&
              properties.length === 0 && (
                <div
                  style={{ marginTop: "8px", fontSize: "14px", color: "#525252" }}
                >
                  No property found.
                </div>
              )}

            {selectedProperty && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "12px",
                  background: "#f4f4f4",
                  border: "1px solid #dcdcdc",
                }}
              >
                <strong>
                  {selectedProperty.title ||
                    selectedProperty.property_title ||
                    "Untitled Property"}
                </strong>
                <div style={{ marginTop: "5px", fontSize: "13px" }}>
                  Sequence ID:{" "}
                  {selectedProperty.property_seq_id ||
                    selectedProperty.property_sequence_id ||
                    "-"}
                </div>
                <div
                  style={{
                    marginTop: "3px",
                    fontSize: "13px",
                    wordBreak: "break-all",
                  }}
                >
                  UUID:{" "}
                  {selectedProperty.uuid ||
                    selectedProperty.propertyUuid ||
                    "-"}
                </div>
                <Button
                  kind="ghost"
                  size="sm"
                  style={{ marginTop: "8px" }}
                  onClick={() => {
                    setSelectedProperty(null);
                    setPropertySearch("");
                    setNewBanner((prev) => ({ ...prev, propertyUuid: "" }));
                  }}
                >
                  Change Property
                </Button>
              </div>
            )}
          </div>

          <div>
            <label
              htmlFor="banner-upload"
              style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}
            >
              {platform === "website"
                ? "Website Banner Image"
                : "Mobile App Banner Image"}{" "}
              <span style={{ color: "#da1e28" }}>*</span>
            </label>

            <input
              type="file"
              id="banner-upload"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleBannerImageChange}
              style={{ display: "none" }}
              disabled={adding}
            />

            <Button
              kind="secondary"
              size="lg"
              onClick={() => document.getElementById("banner-upload").click()}
              disabled={adding}
            >
              {bannerImage ? "Change Image" : "Upload Image"}
            </Button>

            {bannerImage && (
              <div style={{ marginTop: "12px" }}>
                <div style={{ fontSize: "14px", marginBottom: "8px" }}>
                  Selected file: <strong>{bannerImage.name}</strong>{" "}
                  <span style={{ color: "#525252" }}>
                    ({(bannerImage.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>

                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Banner preview"
                    style={{
                      width: "250px",
                      height: "120px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      display: "block",
                    }}
                  />
                )}
              </div>
            )}
          </div>

          <div>
            <label
              htmlFor="banner-order"
              style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}
            >
              Display Order
            </label>
            <input
              id="banner-order"
              type="number"
              value={newBanner.displayOrder}
              onChange={(e) =>
                setNewBanner((prev) => ({
                  ...prev,
                  displayOrder: e.target.value,
                }))
              }
              style={{ width: "100%", padding: "10px", boxSizing: "border-box" }}
            />
          </div>

          <label>
            <input
              type="checkbox"
              checked={newBanner.display}
              onChange={(e) =>
                setNewBanner((prev) => ({
                  ...prev,
                  display: e.target.checked,
                }))
              }
            />{" "}
            Display
          </label>
        </div>
      </Modal>
    </>
  );
}